import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Collapse,
  Grid,
  Group,
  NumberInput,
  Stack,
  Table,
  Title,
  Text,
  Badge,
  Button,
  TextInput,
  MultiSelect,
  Loader,
  Center,
  Alert,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconRefresh,
  IconChartBar,
  IconChevronRight,
  IconAlertCircle,
  IconFilter,
  IconFilterOff,
} from '@tabler/icons-react';
import { depositsApi, type DepositFilter } from '../api/deposits';
import { merchantsApi } from '../api/merchants';
import { PAYOK_PAYMENT_METHODS, type TrxStatus } from '../api/types';
import { formatGmt7, formatIdr, statusColor } from '../lib/format';
import { ApiCallError } from '../api/client';
import { DataTable } from '../components/DataTable';
import { ListPagination } from '../components/ListPagination';
import { ListSummaryStrip } from '../components/ListSummaryStrip';
import { useSession } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { useTranslation } from '../i18n/LocaleContext';

function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Per-column filter state. One field per column rendered in the deposits
 * table. Stored separately from the active `filter` so the form has its own
 * "pending" buffer until the user clicks Apply.
 */
type FilterDraft = {
  trxId: string;
  referenceId: string;
  merchantId: string[]; // internal only
  createdBy: string;
  fromIp: string;
  method: string[];
  trxStatus: string[];
  createdAt: [Date | null, Date | null];
  paidAt: [Date | null, Date | null];
  settleAt: [Date | null, Date | null];
  amountMin: number | '';
  amountMax: number | '';
  feeMin: number | '';
  feeMax: number | '';
  merchantRevenueMin: number | '';
  merchantRevenueMax: number | '';
};

const EMPTY_DRAFT: FilterDraft = {
  trxId: '',
  referenceId: '',
  merchantId: [],
  createdBy: '',
  fromIp: '',
  method: [],
  trxStatus: [],
  createdAt: [null, null],
  paidAt: [null, null],
  settleAt: [null, null],
  amountMin: '',
  amountMax: '',
  feeMin: '',
  feeMax: '',
  merchantRevenueMin: '',
  merchantRevenueMax: '',
};

function draftToFilter(d: FilterDraft): DepositFilter {
  const out: DepositFilter = { limit: 25 };
  if (d.trxId.trim()) out.trxId = d.trxId.trim();
  if (d.referenceId.trim()) out.referenceId = d.referenceId.trim();
  if (d.merchantId.length) out.merchantId = d.merchantId;
  if (d.createdBy.trim()) out.createdBy = d.createdBy.trim();
  if (d.fromIp.trim()) out.fromIp = d.fromIp.trim();
  if (d.method.length) out.method = d.method;
  if (d.trxStatus.length) out.trxStatus = d.trxStatus as TrxStatus[];
  if (d.createdAt[0]) out.createdAtFrom = toIsoDate(d.createdAt[0]);
  if (d.createdAt[1]) out.createdAtTo = toIsoDate(d.createdAt[1]);
  if (d.paidAt[0]) out.paidAtFrom = toIsoDate(d.paidAt[0]);
  if (d.paidAt[1]) out.paidAtTo = toIsoDate(d.paidAt[1]);
  if (d.settleAt[0]) out.settleAtFrom = toIsoDate(d.settleAt[0]);
  if (d.settleAt[1]) out.settleAtTo = toIsoDate(d.settleAt[1]);
  if (d.amountMin !== '') out.amountMin = d.amountMin;
  if (d.amountMax !== '') out.amountMax = d.amountMax;
  if (d.feeMin !== '') out.feeMin = d.feeMin;
  if (d.feeMax !== '') out.feeMax = d.feeMax;
  if (d.merchantRevenueMin !== '') out.merchantRevenueMin = d.merchantRevenueMin;
  if (d.merchantRevenueMax !== '') out.merchantRevenueMax = d.merchantRevenueMax;
  return out;
}

function countActiveFilters(d: FilterDraft): number {
  let n = 0;
  if (d.trxId.trim()) n += 1;
  if (d.referenceId.trim()) n += 1;
  if (d.merchantId.length) n += 1;
  if (d.createdBy.trim()) n += 1;
  if (d.fromIp.trim()) n += 1;
  if (d.method.length) n += 1;
  if (d.trxStatus.length) n += 1;
  if (d.createdAt[0] || d.createdAt[1]) n += 1;
  if (d.paidAt[0] || d.paidAt[1]) n += 1;
  if (d.settleAt[0] || d.settleAt[1]) n += 1;
  if (d.amountMin !== '' || d.amountMax !== '') n += 1;
  if (d.feeMin !== '' || d.feeMax !== '') n += 1;
  if (d.merchantRevenueMin !== '' || d.merchantRevenueMax !== '') n += 1;
  return n;
}

export function DepositsPage() {
  const navigate = useNavigate();
  const { state } = useSession();
  const { t } = useTranslation();
  const isPlatformUser =
    state.status === 'authenticated' && isPlatform(state.profile);

  const [filter, setFilter] = useState<DepositFilter>({ limit: 25 });
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Overview strip is collapsed by default (toggle in the page header); its
  // summary request only runs while it is open.
  const [overviewOpen, setOverviewOpen] = useState(false);

  // Internal users get a merchant filter dropdown — fetch the list.
  const merchantsQuery = useQuery({
    queryKey: ['merchants-for-filter'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: isPlatformUser,
  });

  const queryFilter: DepositFilter = {
    ...filter,
    page,
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['deposits', queryFilter],
    queryFn: () => depositsApi.page(queryFilter),
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const applyFilters = () => {
    setPage(1);
    setFilter(draftToFilter(draft));
  };

  const clearFilters = () => {
    setDraft(EMPTY_DRAFT);
    setPage(1);
    setFilter({ limit: 25 });
  };

  const activeCount = countActiveFilters(draft);
  const setField = <K extends keyof FilterDraft>(k: K, v: FilterDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  // Filtered summary (overview strip): same filter, pagination stripped.
  const summaryQuery = useQuery({
    queryKey: ['deposits-summary', filter],
    queryFn: () => depositsApi.summary(filter),
    enabled: overviewOpen,
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const pagination = (
    <ListPagination
      page={page}
      pageCount={data?.pageCount ?? 1}
      total={data?.total ?? 0}
      limit={filter.limit ?? 25}
      loading={isFetching}
      onPageChange={setPage}
      onLimitChange={(limit) => {
        setPage(1);
        setFilter((f) => ({ ...f, limit }));
      }}
    />
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('deposits.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('deposits.subtitle')}
          </Text>
        </div>
        <Group gap="xs">
          <Tooltip label={t('list.toggleOverview')}>
            <ActionIcon
              variant={overviewOpen ? 'filled' : 'default'}
              aria-pressed={overviewOpen}
              aria-label={t('list.toggleOverview')}
              onClick={() => setOverviewOpen((v) => !v)}
            >
              <IconChartBar size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('list.toggleFilters')}>
            <ActionIcon
              variant={filtersOpen ? 'filled' : 'default'}
              aria-pressed={filtersOpen}
              aria-label={t('list.toggleFilters')}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <IconFilter size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('common.refresh')}>
            <ActionIcon variant="default" onClick={() => void refetch()} loading={isFetching}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Collapse in={overviewOpen}>
      <ListSummaryStrip
        summary={summaryQuery.data}
        loading={summaryQuery.isLoading}
        error={summaryQuery.error}
      />
      </Collapse>

      <Card>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" fw={600}>{t('deposits.filters')}</Text>
              {activeCount > 0 && (
                <Badge color="brand" variant="light" size="sm">
                  {t('deposits.activeFilters', { n: activeCount })}
                </Badge>
              )}
            </Group>
            <Group gap={6}>
              <Button
                size="xs"
                variant="subtle"
                leftSection={filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                {filtersOpen ? t('common.collapse') : t('common.show')}
              </Button>
            </Group>
          </Group>
          <Collapse in={filtersOpen}>
            <Stack gap="md">
              {/* Identifiers row */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('deposits.colTrxId')}
                    placeholder={t('deposits.placeholderTrxId')}
                    value={draft.trxId}
                    onChange={(e) => setField('trxId', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('deposits.colMerchantReff')}
                    placeholder={t('deposits.placeholderReff')}
                    value={draft.referenceId}
                    onChange={(e) => setField('referenceId', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('deposits.colCreatedBy')}
                    placeholder={t('deposits.colCreatedByPartial')}
                    value={draft.createdBy}
                    onChange={(e) => setField('createdBy', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('deposits.colRequestedFrom')}
                    placeholder={t('deposits.placeholderIp')}
                    value={draft.fromIp}
                    onChange={(e) => setField('fromIp', e.currentTarget.value)}
                  />
                </Grid.Col>
              </Grid>

              {/* Categorical row — Merchant filter visible only to internal users */}
              <Grid>
                {isPlatformUser && (
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <MultiSelect
                      label={t('deposits.colMerchantName')}
                      placeholder={
                        draft.merchantId.length > 0
                          ? undefined
                          : merchantsQuery.isLoading
                            ? t('deposits.placeholderLoading')
                            : t('deposits.placeholderAllMerchants')
                      }
                      data={(merchantsQuery.data?.rows ?? []).map((m) => ({
                        value: m.id,
                        label: m.name,
                      }))}
                      value={draft.merchantId}
                      onChange={(v) => setField('merchantId', v)}
                      searchable
                      clearable
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={{ base: 12, md: 6, lg: isPlatformUser ? 4 : 6 }}>
                  <MultiSelect
                    label={t('deposits.colMethod')}
                    placeholder={draft.method.length > 0 ? undefined : t('deposits.placeholderAllMethods')}
                    data={[...PAYOK_PAYMENT_METHODS]}
                    value={draft.method}
                    onChange={(v) => setField('method', v)}
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: isPlatformUser ? 4 : 6 }}>
                  <MultiSelect
                    label={t('deposits.colStatus')}
                    placeholder={draft.trxStatus.length > 0 ? undefined : t('deposits.placeholderAllStatuses')}
                    data={['pending', 'paid', 'failed']}
                    value={draft.trxStatus}
                    onChange={(v) => setField('trxStatus', v)}
                    clearable
                  />
                </Grid.Col>
              </Grid>

              {/* Date ranges — three independent windows */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <DatePickerInput
                    type="range"
                    allowSingleDateInRange
                    label={t('deposits.createdAtLabel')}
                    placeholder={t('deposits.placeholderDateRange')}
                    value={draft.createdAt}
                    onChange={(v) => setField('createdAt', v as [Date | null, Date | null])}
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <DatePickerInput
                    type="range"
                    allowSingleDateInRange
                    label={t('deposits.paidAtLabel')}
                    placeholder={t('deposits.placeholderDateRange')}
                    value={draft.paidAt}
                    onChange={(v) => setField('paidAt', v as [Date | null, Date | null])}
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <DatePickerInput
                    type="range"
                    allowSingleDateInRange
                    label={t('deposits.settleAtLabel')}
                    // No `description` here — it adds a second text line that
                    // would push this input down vs the other two date pickers
                    // and break the row alignment. The column header in the
                    // table already conveys the meaning.
                    placeholder={t('deposits.placeholderDateRange')}
                    value={draft.settleAt}
                    onChange={(v) => setField('settleAt', v as [Date | null, Date | null])}
                    clearable
                  />
                </Grid.Col>
              </Grid>

              {/* Numeric ranges. Three ranges with min/max each = 6 inputs */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Text size="xs" c="dimmed" fw={600} mb={4}>{t('deposits.amountLabel')}</Text>
                  <Group grow gap="xs">
                    <NumberInput
                      placeholder={t('deposits.placeholderMin')}
                      min={0}
                      thousandSeparator=","
                      value={draft.amountMin}
                      onChange={(v) => setField('amountMin', typeof v === 'number' ? v : '')}
                    />
                    <NumberInput
                      placeholder={t('deposits.placeholderMax')}
                      min={0}
                      thousandSeparator=","
                      value={draft.amountMax}
                      onChange={(v) => setField('amountMax', typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Text size="xs" c="dimmed" fw={600} mb={4}>{t('deposits.feeLabel')}</Text>
                  <Group grow gap="xs">
                    <NumberInput
                      placeholder={t('deposits.placeholderMin')}
                      min={0}
                      thousandSeparator=","
                      value={draft.feeMin}
                      onChange={(v) => setField('feeMin', typeof v === 'number' ? v : '')}
                    />
                    <NumberInput
                      placeholder={t('deposits.placeholderMax')}
                      min={0}
                      thousandSeparator=","
                      value={draft.feeMax}
                      onChange={(v) => setField('feeMax', typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Text size="xs" c="dimmed" fw={600} mb={4}>{t('deposits.merchantRevenueLabel')}</Text>
                  <Group grow gap="xs">
                    <NumberInput
                      placeholder={t('deposits.placeholderMin')}
                      min={0}
                      thousandSeparator=","
                      value={draft.merchantRevenueMin}
                      onChange={(v) => setField('merchantRevenueMin', typeof v === 'number' ? v : '')}
                    />
                    <NumberInput
                      placeholder={t('deposits.placeholderMax')}
                      min={0}
                      thousandSeparator=","
                      value={draft.merchantRevenueMax}
                      onChange={(v) => setField('merchantRevenueMax', typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Grid.Col>
              </Grid>

              <Group justify="flex-end">
                <Button variant="default" onClick={clearFilters}>
                  {t('common.reset')}
                </Button>
                <Button onClick={applyFilters}>{t('common.apply')}</Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </Card>

      {/* overflow visible: the DataTable's sticky proxy scrollbar must not be
          clipped by the Card (Mantine Card is overflow:hidden by default). */}
      <Card p={0} style={{ overflow: 'visible' }}>
        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            {error instanceof ApiCallError ? `${error.code}: ${error.message}` : t('common.unknown')}
          </Alert>
        )}
        {!isLoading && !error && (
          // Wide grid: sticky proxy scrollbar, column chooser, resizable columns,
          // trxId pinned left + merchant revenue / action pinned right. See DataTable.
          <DataTable
            tableId="deposits"
            toolbar={pagination}
            columns={[
              { key: 'trxId', label: t('deposits.colTrxId'), width: 290, sticky: 'left' },
              { key: 'ref', label: t('deposits.colMerchantReff'), width: 190 },
              { key: 'merchant', label: t('deposits.colMerchantName'), width: 190 },
              { key: 'createdAt', label: t('deposits.colCreatedAt'), width: 150 },
              { key: 'createdBy', label: t('deposits.colCreatedBy'), width: 210 },
              { key: 'fromIp', label: t('deposits.colRequestedFrom'), width: 130 },
              { key: 'method', label: t('deposits.colMethod'), width: 110 },
              { key: 'status', label: t('deposits.colStatus'), width: 120 },
              { key: 'paidAt', label: t('deposits.colPaidAt'), width: 150 },
              { key: 'settleAt', label: t('deposits.colSettleDate'), width: 150 },
              { key: 'settlement', label: t('deposits.colSettlement'), width: 170 },
              { key: 'amount', label: t('deposits.colAmount'), width: 140, align: 'right' },
              { key: 'fee', label: t('deposits.colFee'), width: 120, align: 'right' },
              { key: 'revenue', label: t('deposits.colMerchantRevenue'), width: 160, align: 'right', sticky: 'right' },
              { key: 'action', width: 44, fixed: true, sticky: 'right' },
            ]}
          >
                {data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={15}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('deposits.empty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {data?.rows.map((d) => {
                  // Merchant Revenue color reflects whether the funds have
                  // actually moved to activeBalance. `paid` alone is no longer
                  // enough — settlement now happens later via the worker
                  // (settledAt is set when wallet move completes).
                  const isSettled = d.trxStatus === 'paid' && d.settledAt != null;
                  const revenueColor = isSettled ? 'green.7' : undefined;
                  const revenueWeight = isSettled ? 600 : 400;
                  return (
                    <Table.Tr
                      key={d.trxId}
                      onClick={() => navigate(`/dashboard/deposits/${d.trxId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {d.trxId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.referenceId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{d.merchantName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{formatGmt7(d.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm">{d.createdBy}</Text>
                          <Text size="xs" c="dimmed">{d.merchantEmail}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" c="dimmed">{d.fromIp || '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="default" size="sm">{d.method}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColor(d.trxStatus)} variant="light">
                          {d.trxStatus}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{d.paidAt ? formatGmt7(d.paidAt) : '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {/* Settle Date column — scheduled settle_at, regardless
                            of whether the worker has actually run yet. Lets you
                            scan ahead for "what settles tomorrow". */}
                        <Text size="xs" c="dimmed">{d.settleAt ? formatGmt7(d.settleAt) : '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {/* Settlement state derived from settleAt / settledAt.
                            paid + settledAt set       → "settled" (green, with timestamp)
                            paid + settleAt only       → "awaiting" (yellow, shows scheduled time)
                            anything else (not paid)   → em-dash */}
                        {d.trxStatus !== 'paid' ? (
                          <Text size="xs" c="dimmed">—</Text>
                        ) : d.settledAt ? (
                          <div>
                            <Badge color="green" variant="light" size="xs">{t('deposits.settlementSettled')}</Badge>
                            <Text size="xs" c="dimmed" mt={2}>{formatGmt7(d.settledAt)}</Text>
                          </div>
                        ) : d.settleAt ? (
                          <div>
                            <Badge color="yellow" variant="light" size="xs">{t('deposits.settlementAwaiting')}</Badge>
                            <Text size="xs" c="dimmed" mt={2}>{formatGmt7(d.settleAt)}</Text>
                          </div>
                        ) : (
                          <Text size="xs" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatIdr(d.amount)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c="dimmed">{formatIdr(d.fee)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c={revenueColor} fw={revenueWeight}>
                          {isSettled ? '+ ' : ''}{formatIdr(d.merchantRevenue)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <IconChevronRight size={16} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
          </DataTable>
        )}
        <Group justify="flex-end" p="md">
          {pagination}
        </Group>
      </Card>
    </Stack>
  );
}
